/**
 * StatusDonut renders a donut chart showing distribution of a categorical field.
 * Uses centralized color constants from src/constants for all known label types
 * (publishers, campaign statuses, workflow statuses, asset statuses, business lines).
 */
import React from 'react';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Text from '@amzn/meridian/text';
import Row from '@amzn/meridian/row';
import { VictoryPie } from 'victory';
import {
  PUBLISHER_CHART_COLORS,
  CAMPAIGN_STATUS_COLORS,
  WORKFLOW_STATUS_COLORS,
  ASSET_STATUS_COLORS,
  BUSINESS_LINE_COLORS,
  WORKFLOW_TYPE_COLORS,
} from 'src/constants';
import { skeletonBase } from 'src/components/entity-metrics/skeleton';
import { useTranslation } from 'src/arb-wrapper';

const FALLBACK_COLOR = '#6B7280';

/** Resolve the chart color for a given label across all known color schemes */
const getColor = (label: string): string => {
  const upper = label.toUpperCase();
  const lower = label.toLowerCase();
  const normalized = upper.replace(/\s+/g, '_');

  // Direct match across all maps (including normalized with underscores)
  const direct =
    CAMPAIGN_STATUS_COLORS[label] ??
    WORKFLOW_STATUS_COLORS[label] ??
    WORKFLOW_TYPE_COLORS[label] ??
    ASSET_STATUS_COLORS[label] ??
    BUSINESS_LINE_COLORS[upper] ??
    BUSINESS_LINE_COLORS[normalized] ??
    PUBLISHER_CHART_COLORS[lower];
  if (direct) return direct;

  // Fuzzy match for compound labels (e.g., "Sports SVOD", "Sports Channel")
  for (const [key, color] of Object.entries(BUSINESS_LINE_COLORS)) {
    if (upper.includes(key)) return color;
  }
  for (const [key, color] of Object.entries(PUBLISHER_CHART_COLORS)) {
    if (lower.includes(key)) return color;
  }

  return FALLBACK_COLOR;
};

interface StatusDonutProps {
  data: Array<{ label: string; value: number }>;
  title: string;
  isLoading?: boolean;
}

export const StatusDonut: React.FC<StatusDonutProps> = ({
  data,
  title,
  isLoading,
}) => {
  const { t } = useTranslation();
  const colorScale = data.map((d) => getColor(d.label));
  const total = data.reduce((sum, d) => sum + d.value, 0);

  return (
    <Column
      backgroundColor="white"
      spacing="300"
      spacingInset="400"
      type="outline"
    >
      <Heading level={4}>{title}</Heading>
      {isLoading
        ? (
        <Row alignmentHorizontal="center" spacing="400">
          <div
            style={{
              ...skeletonBase,
              width: 200,
              height: 200,
              borderRadius: '50%',
            }}
          />
          <Column spacing="200">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                style={{
                  height: 16,
                  width: 120,
                  borderRadius: 4,
                  background: '#f0f0f0',
                }}
              />
            ))}
          </Column>
        </Row>
          )
        : data.length === 0
          ? (
        <Text color="secondary">{t('chart_no_data_available')}</Text>
            )
          : (
        <Row alignmentHorizontal="center" spacing="400">
          <div style={{ width: 200, height: 200 }}>
            <VictoryPie
              colorScale={colorScale}
              data={data.map((d) => ({ x: d.label, y: d.value }))}
              innerRadius={60}
              labels={() => ''}
              padding={10}
            />
          </div>
          <Column spacing="200">
            {data.map((d) => (
              <Row key={d.label} spacing="200">
                <div
                  style={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: getColor(d.label),
                    flexShrink: 0,
                  }}
                />
                <Text type="b200">
                  {d.label}: {d.value} (
                  {total > 0 ? Math.round((d.value / total) * 100) : 0}%)
                </Text>
              </Row>
            ))}
          </Column>
        </Row>
            )}
    </Column>
  );
};
